import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { getEventById } from "../../Services/Event";
import { IEvent } from "../../interfaces/models/IEvent";
import {
  attendeesStatusEnum,
  attendeesTypeEnum,
  paymentMethodEnum,
  statusEnum,
} from "../../Enums/RegistrationEnum";

import { formatTimeSpanToString } from "../../utils/functions";
import { getAllFirmNames } from "../../Services/Firm";
import {
  buildFirmShortLabelIndex,
  firmShortLabel,
  lookupFirmShortLabel,
  type FirmShortLabelIndex,
} from "../../utils/firmDisplayName";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import "../EventsPage/Events.css";
import PencilEdit from "../../components/Atomos/Icons/PencilEdit";
import Pencil from "../../components/Atomos/Icons/ViewMode/Pencil";
import EventAttendeesDashboard from "./components/EventAttendeesDashboard";
import { getDashboardAttendees } from "../../Services/Registration";
import { getOfficeJurisdictionFilterData } from "../../Services/Office";
import { IDashboardData } from "../../interfaces/models/IDashboardData";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import type {
  IFilterData,
  IHeader,
  IQuickFilter,
} from "../../components/Organismos/Table/FilteredTable.types";
import {
  FIELDS_TO_IGNORE,
  getExcelColumnTitle,
} from "../../components/Organismos/Table/FilteredTable.utils";
import Button from "../../components/staff/Button";
import Plus from "../../components/Atomos/Icons/EditMode/Plus";
import {
  EVENT_ATTENDEES_CANCELLED_QUICK_FILTER,
  EVENT_ATTENDEES_PARTICIPANTS_QUICK_FILTER,
  EVENT_ATTENDEES_SPECIAL_EVENTS_QUICK_FILTER,
  REGISTRATION_WSG_STAFF_QUICK_FILTER,
  SPECIAL_EVENT_FILTER_OPTION,
} from "./registrationQuickFilters";
import Stats from "../../components/Atomos/Icons/Stats";
import { timestampToDateString } from "../../utils/dates";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import type { ISorting } from "../../interfaces/Components/ITable";
import { resolveFirmJurisdictionLabel } from "../../utils/resolveFirmJurisdictionDisplay";

export const EVENT_ATTENDEES_TABLE_KEY = "/events-staff/event-attendees";

const EVENT_ATTENDEES_DEFAULT_SORTING: ISorting = {
  field: "registrationDate",
  order: "desc",
};

const statusColors = {
  Cancelled: "text-orange-600",
  Attending: "text-green-600",
} as const;

const { guest, ...attendeesTypeEnumWithoutGuest } = attendeesTypeEnum;

const GUEST_SPEAKER_REG_TYPE_LABEL = "Guest SPK";

function relabelFirmText(index: FirmShortLabelIndex, value: unknown): string {
  return lookupFirmShortLabel(index, value);
}

function relabelRoleRow(index: FirmShortLabelIndex, value: unknown): unknown {
  if (!value || typeof value !== "object") {
    return value;
  }
  const row = value as Record<string, unknown>;
  const next = { ...row };
  if ("firm" in next) {
    next.firm = relabelFirmText(index, next.firm);
  }
  if ("firmName" in next) {
    next.firmName = relabelFirmText(index, next.firmName);
  }
  return next;
}

function relabelDashboardFirmNames(
  data: IDashboardData | null,
  index: FirmShortLabelIndex,
): IDashboardData | null {
  if (!data) {
    return data;
  }
  const firm = (value: unknown) => relabelFirmText(index, value);
  return {
    ...data,
    attendeesByBadgeName: (data.attendeesByBadgeName ?? []).map((row) => ({
      ...row,
      firm: firm(row.firm),
    })),
    attendeesGuests: (data.attendeesGuests ?? []).map((row) => ({
      ...row,
      firm: firm(row.firm),
    })),
    totalPaidRegistrants: (data.totalPaidRegistrants ?? []).map((row) => ({
      ...row,
      firm: firm(row.firm),
    })),
    jurisdictionOffices: data.jurisdictionOffices?.map((row) => ({
      ...row,
      firm: firm(row.firm),
    })),
    uniqueFirmsList: data.uniqueFirmsList?.map((row) => ({
      ...row,
      firm: firm(row.firm),
    })),
    leadershipAttendees: (data.leadershipAttendees ?? []).map((region) => ({
      ...region,
      firms: (region.firms ?? []).map((row) => ({
        ...row,
        firm: firm(row.firm),
      })),
    })),
    registrationByRegionalRepresentation: (
      data.registrationByRegionalRepresentation ?? []
    ).map((region) => ({
      ...region,
      offices: (region.offices ?? []).map((office) => ({
        ...office,
        name: firm(office.name),
        firmName: firm(office.firmName),
      })),
      firms: region.firms?.map((row) => ({
        ...row,
        firm: firm(row.firm),
        firmName: firm(row.firmName),
      })),
    })),
    attendeeRoles: (data.attendeeRoles ?? []).map((role) => ({
      ...role,
      value: (role.value ?? []).map((row) => relabelRoleRow(index, row)),
    })),
    specialRequests: data.specialRequests?.map((row) => ({
      ...row,
      firmName: firm(row.firmName),
    })),
    specialEvents: data.specialEvents?.map((eventRow) => ({
      ...eventRow,
      attendees: eventRow.attendees?.map((row) => ({
        ...row,
        firm: firm(row.firm),
      })),
      guests: eventRow.guests?.map((row) => ({
        ...row,
        firm: firm(row.firm),
      })),
      staffAttendees: eventRow.staffAttendees?.map((row) => ({
        ...row,
        firm: firm(row.firm),
      })),
      staffGuests: eventRow.staffGuests?.map((row) => ({
        ...row,
        firm: firm(row.firm),
      })),
    })),
  };
}

const formatAttendeeTypeForDisplay = (raw: string | undefined): string => {
  const key = raw === "member" ? "regular" : raw;
  if (key === "guest" || key === "Guest") {
    return GUEST_SPEAKER_REG_TYPE_LABEL;
  }
  return attendeesTypeEnum[key as keyof typeof attendeesTypeEnum] ?? raw ?? "";
};
export default function EventAttendees (): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const [event, setEvent] = useState<IEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dashboardAttendees, setDashboardAttendees] =
    useState<IDashboardData | null>(null);
  const {
    userPreferences,
    patchUserPreferences,
    loading: preferencesLoading,
  } = useUserPreferences();
  const showDashboard =
    userPreferences?.preferences?.attendeesShowDashboard ?? true;
  const dashboardPreferenceKnown = !preferencesLoading;
  const [persistentFilter] = useState<any>({
    eventId: { value: eventId, comparisonType: "EQUAL" },
  });
  const [firmData, setFirmData] = useState<any[]>([]);
  const firmShortIndex = useMemo(
    () => buildFirmShortLabelIndex(firmData),
    [firmData],
  );
  const dashboardWithShortNames = useMemo(
    () => relabelDashboardFirmNames(dashboardAttendees, firmShortIndex),
    [dashboardAttendees, firmShortIndex],
  );
  const [jurisdictionData, setJurisdictionData] = useState<
    { id: string; name: string }[]
  >([]);
  const [jurisdictionCatalog, setJurisdictionCatalog] = useState<
    { id: string; name: string }[]
  >([]);
  const { regionData } = UseRegionList();
  const endpoint = "/registration";
  const isFetchingEventData = useRef<boolean>(false);

  const fetchEventData = useCallback(async () => {
    if (isFetchingEventData.current) {
      return;
    }

    if (!eventId) {
      setError("Event ID is required");
      return;
    }

    isFetchingEventData.current = true;
    try {
      // This page only reads base fields (title, startDate, setupType, shortId); skip the
      // withWSG enrichment (speaker firms / contactUs / template) to keep the event load light.
      let eventData = await getEventById(eventId, false);
      if (!eventData) {
        throw new Error(`Event with ID ${eventId} not found`);
      }
      setEvent(eventData);
    } catch (err) {
      setError("Failed to load event");
    } finally {
      isFetchingEventData.current = false;
    }
  }, [eventId]);

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  useEffect(() => {
    let active = true;
    getAllFirmNames()
      .then((firms) => {
        if (active) {
          setFirmData(Array.isArray(firms) ? firms : []);
        }
      })
      .catch(() => {
        if (active) {
          setFirmData([]);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getOfficeJurisdictionFilterData()
      .then(({ options, catalog }) => {
        if (!active) {
          return;
        }
        setJurisdictionData(options);
        setJurisdictionCatalog(catalog);
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setJurisdictionData([]);
        setJurisdictionCatalog([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const lastDashboardFetchKey = useRef<string | null>(null);

  const getDashboardData = useCallback(async (eventId: string) => {
    const dashboardData = await getDashboardAttendees(eventId);
    setDashboardAttendees(dashboardData);
  }, []);

  useEffect(() => {
    if (!eventId || !dashboardPreferenceKnown || !showDashboard) {
      return;
    }
    const fetchKey = `${eventId}`;
    if (lastDashboardFetchKey.current === fetchKey && dashboardAttendees != null) {
      return;
    }
    // Defer dashboard stats so the registration table request starts first.
    const timeoutId = window.setTimeout(() => {
      lastDashboardFetchKey.current = fetchKey;
      void getDashboardData(eventId);
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [
    eventId,
    dashboardPreferenceKnown,
    showDashboard,
    getDashboardData,
    dashboardAttendees,
  ]);

  const columnsDef = useMemo(
    () => [
      {
        id: "actions",
        header: "Edit",
        enableSorting: false,
        columnOrderFixed: true,
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
          const registrationId = row.id;
          return (
            <Link
              to={`/events-staff/${eventId}/attendees/Edit/${registrationId}`}
              className="flex flex-row items-center cursor-pointer hover:opacity-80"
            >
              <div className="w-5 h-5 [&>div>svg]:w-full [&>div>svg]:h-full [&>div>svg>path]:fill-current">
                <PencilEdit />
              </div>
            </Link>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
          const value = row.status;
          return (
            <span
              className={`${statusColors[value as keyof typeof statusColors]} font-medium`}
            >
              {value}
            </span>
          );
        },
      },
      { id: "attendeeType", header: "Att Type" },
      { id: "registrationDate", header: "Reg Date" },
      { id: "personName", header: "Name", width: 140 },
      {
        id: "personLastName",
        header: "Last Name",
        defaultVisibility: false,
      },
      {
        id: "personEmail",
        header: "Email",
        defaultVisibility: false,
      },
      { id: "badgeName", header: "Badge Name", defaultVisibility: false },
      { id: "firmName", header: "Firm", width: 130 },
      { id: "mainOfficeName", header: "Jurisdiction" },
      { id: "countryName", header: "Country" },
      {
        id: "firmRegion",
        header: "Region",
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
          const value = row.firmRegion;
          if (value?.toLowerCase().includes("north america")) {
            return "North America";
          }
          if (value?.toLowerCase().includes("latin america")) {
            return value.split(",")[1].trim();
          }
          return value;
        },
      },
      { id: "totalAmount", header: "Amount", enableSorting: false },
      { id: "prepaidStatus", header: "Prepaid", defaultVisibility: false },
      { id: "paymentMethod", header: "Pyt Type" },
      { id: "paymentStatus", header: "Paid" },
      {
        id: "guestNumber",
        header: "Guests",
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
          const value = row.guestNumber;
          return (
            <div className="flex justify-center items-center w-full h-full">
              <span className="text-gray-900 font-medium text-center">
                {value}
              </span>
            </div>
          );
        },
      },
      { id: "registrationNotes", header: "Registration Notes" },
      { id: "shortId", header: "Reg ID" },
    ],
    [eventId],
  );

  const statusOptions = useMemo(
    () => [
      { id: "attending", name: "Attending" },
      { id: "cancelled", name: "Cancelled" },
    ],
    [],
  );
  const paidStatusOptions = Object.entries(statusEnum)
    .filter(([id]) => id !== "unpaidSPK")
    .map(([id, name]) => ({
      id,
      name,
    }));
  const paymentTypeOptions = Object.entries(paymentMethodEnum).map(
    ([id, name]) => ({
      id,
      name,
    }),
  );

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        name: "Firm",
        fieldName: "firmId",
        objectData:
          firmData?.map((f: any) => ({
            id: f.id,
            name: firmShortLabel(f),
          })) || [],
        comparison: "EQUAL",
      },
      {
        name: "Jurisdiction",
        fieldName: "mainOfficeName",
        objectData:
          jurisdictionData?.map((c: any) => ({ id: c.id, name: c.name })) || [],
        comparison: "BY_NAME",
      },
      {
        name: "Region",
        fieldName: "firmRegion",
        objectData:
          regionData?.map((r: any) => ({ id: r.id, name: r.name })) || [],
        comparison: "BY_NAME_CONTAINS",
      },
      {
        name: "Status",
        fieldName: "status",
        objectData: statusOptions,
        comparison: "EQUAL",
      },
      {
        name: "Special Event",
        fieldName: "selectedSpecialEventIds",
        objectData: [SPECIAL_EVENT_FILTER_OPTION],
        comparison: "IS_DEFINED",
        disabledWrite: true,
      },
      {
        name: "Paid",
        fieldName: "paymentStatus",
        type: "multiselect",
        objectData: paidStatusOptions,
        comparison: "EQUAL",
        disabledWrite: true,
      },
      {
        name: "Payment Type",
        fieldName: "paymentMethod",
        objectData: paymentTypeOptions,
        comparison: "EQUAL",
      },
      ...(event?.setupType === "annual"
        ? [
          {
            name: "Prepaid Attendee Status",
            fieldName: "prepaid",
            objectData: [
              { id: "yes", name: "Yes" },
              { id: "no", name: "No" },
            ],
            comparison: "BOOLEAN" as const,
            disabledWrite: true,
          },
        ]
        : []),
      {
        name: "Attendee Type",
        fieldName: "attendeeType",
        objectData: Object.entries(attendeesTypeEnumWithoutGuest).map(
          ([id, name]) => ({
            id,
            name,
          }),
        ),
        comparison: "EQUAL",
      },
    ],
    [
      firmData,
      jurisdictionData,
      regionData,
      statusOptions,
      paidStatusOptions,
      paymentTypeOptions,
      event?.setupType,
    ],
  );

  const formatData = useCallback(
    (dataToFormat: any[]) => {
      if (!dataToFormat || !Array.isArray(dataToFormat)) {
        return [];
      }
      return dataToFormat.map((d: any) => {
        const firstName = d.personName ?? "";
        const lastName = d.personLastName ?? "";
        const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
        const fromMain =
          typeof d.mainOfficeName === "string" ? d.mainOfficeName.trim() : "";
        const jurisdictionColumn =
          fromMain ||
          resolveFirmJurisdictionLabel(
            d.officeJurisdiction,
            jurisdictionCatalog,
          );
        return {
          ...d,
          firmName: lookupFirmShortLabel(
            firmShortIndex,
            d.firmName,
            d.firmId || d.firm,
          ),
          personName: fullName || "-",
          mainOfficeName: jurisdictionColumn,
          status:
            attendeesStatusEnum[d.status as keyof typeof attendeesStatusEnum],
          prepaidStatus: d.prepaid ? "Yes" : "No",
          paymentMethod:
            paymentMethodEnum[
            d.paymentMethod as keyof typeof paymentMethodEnum
            ],
          paymentStatus: statusEnum[d.paymentStatus as keyof typeof statusEnum],
          registrationDate: formatTimeSpanToString(d.registrationDate),
          attendeeType: formatAttendeeTypeForDisplay(d.attendeeType),
          amountPaid: `$${d.amountPaid ?? 0}`,
        };
      });
    },
    [firmShortIndex, jurisdictionCatalog],
  );

  const formatExcelData = useCallback(
    (dataToFormat: any[], isCurrentData?: boolean) => {
      if (!dataToFormat?.length) {
        return [];
      }
      const rows = isCurrentData ? dataToFormat : formatData(dataToFormat);
      return rows.map((row) => ({
        ...row,
        personName: row.personName ?? "",
        personLastName: row.personLastName ?? "",
        email: row.personEmail ?? "",
        registrationDate: formatTimeSpanToString(row.registrationDate),
      }));
    },
    [formatData],
  );

  const excelHeaders = useMemo(
    () =>
      columnsDef
        .filter((col) => !FIELDS_TO_IGNORE.has(col.id))
        .map((col) => getExcelColumnTitle(col as IHeader)),
    [columnsDef],
  );

  const handleShowDashboardToggle = async () => {
    const next = !showDashboard;
    await patchUserPreferences({
      preferences: {
        ...userPreferences?.preferences,
        attendeesShowDashboard: next,
      },
    });
  };

  const eventTitle = event?.title || event?.shortId || `Event ${eventId}`;
  const eventSubtitle = event?.startDate
    ? timestampToDateString(event.startDate)
    : null;
  const excelFilename = useMemo(() => {
    const d = new Date();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    const safeName =
      (eventTitle || "Event")
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, " ")
        .trim() || "Event";
    return `${safeName} - ${month}-${day}-${year}.xlsx`;
  }, [eventTitle]);

  const quickFilters: IQuickFilter[] = [
    EVENT_ATTENDEES_PARTICIPANTS_QUICK_FILTER,
    EVENT_ATTENDEES_SPECIAL_EVENTS_QUICK_FILTER,
    { ...REGISTRATION_WSG_STAFF_QUICK_FILTER, label: "WSG Staff" },
    EVENT_ATTENDEES_CANCELLED_QUICK_FILTER,
  ];

  const dashboardBanner = useMemo(
    () =>
      showDashboard ? (
        <EventAttendeesDashboard data={dashboardWithShortNames} />
      ) : null,
    [showDashboard, dashboardWithShortNames],
  );

  useEffect(() => {
    if (!eventId) {
      return;
    }

    setPageMeta({
      title: event?.title || "Loading",
      subtitle: eventSubtitle || "",
      breadcrumbLabels: {
        [`/events-staff/${eventId}`]: event?.title || "Loading",
      },
      breadcrumbAction: {
        label: "Modify Event",
        onClick: () => navigate(`/events-staff/${eventId}`),
        icon: <Pencil />,
      },
    });

    return () => {
      clearPageMeta();
    };
  }, [
    clearPageMeta,
    event?.title,
    eventId,
    eventSubtitle,
    eventTitle,
    navigate,
    setPageMeta,
  ]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-lg text-red-600">{error}</div>
        <button
          onClick={() => navigate("/events")}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to Events
        </button>
      </div>
    );
  }

  return (
    <FilteredTableCore
      tableKey={EVENT_ATTENDEES_TABLE_KEY}
      columnsDef={columnsDef}
      showRowNumber
      endpoint={endpoint}
      seedDefaultSortingPreference
      stickyQuickFilterBar
      quickFilterBarPosition="both"
      quickFilterBarAccessory={
        <Button
          text={`${showDashboard ? "Hide Stats" : "Show Stats"}`}
          color="white"
          variant="outlined"
          size="fixed"
          onClick={() => handleShowDashboardToggle()}
          icon={<Stats />}
        />
      }
      searchToolbarAccessory={
        <Button
          text="Add Participant"
          color="secondary"
          variant="filled"
          size="fixed"
          onClick={() => navigate(`/events-staff/${eventId}/attendees/Create`)}
          icon={<Plus />}
        />
      }
      filtersData={filtersData}
      quickFilters={quickFilters}
      quickFilterRequireSelection
      persistentFilter={persistentFilter}
      formatData={formatData}
      excelFilename={excelFilename}
      showExcelExport
      formatExcelData={formatExcelData}
      excelHeaders={excelHeaders}
      showSearchInput
      searchPlaceholder="Search by Name, Event, Reg Type, Firm"
      bannerContent={dashboardBanner}
      defaultPageSize={200}
      defaultSorting={EVENT_ATTENDEES_DEFAULT_SORTING}
    />
  );
}
